import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import './ResetPasswordForm.css';

interface ResetPasswordFormData {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Get email from URL params if available
  const emailFromUrl = useMemo(() => searchParams.get('email') || '', [searchParams]);

  useEffect(() => {
    if (emailFromUrl) {
      setFormData((prev) => ({ ...prev, email: emailFromUrl }));
    }
  }, [emailFromUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = '請輸入 Email';
    } else if (!formData.email.includes('@')) {
      newErrors.email = '請輸入有效的 Email';
    }

    if (!formData.code) {
      newErrors.code = '請輸入驗證碼';
    } else if (formData.code.length !== 6) {
      newErrors.code = '驗證碼應為 6 位數';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = '請輸入新密碼';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = '密碼至少需要 6 個字元';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '請確認新密碼';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '兩次輸入的密碼不一致';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await api.post('/api/members/reset-password', {
        email: formData.email.toLowerCase().trim(),
        code: formData.code.trim(),
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      if (result.success) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(result.error?.message || '重設失敗，請稍後再試。');
      }
    } catch {
      setSubmitError('發生錯誤，請稍後再試。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  const handleBackToForgot = useCallback(() => {
    navigate('/forgot-password', { state: { email: formData.email } });
  }, [navigate, formData.email]);

  if (submitSuccess) {
    return (
      <div className="reset-password-form">
        <div className="reset-password-form__header">
          <div className="reset-password-form__success-icon">✅</div>
          <h2 className="reset-password-form__title">密碼重設成功</h2>
        </div>

        <div className="reset-password-form__success-message">
          <p>您的密碼已成功重設。</p>
          <p>請使用新密碼登入您的帳號。</p>
        </div>

        <div className="reset-password-form__actions">
          <button
            type="button"
            className="reset-password-form__primary-button"
            onClick={handleBackToLogin}
          >
            前往登入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-form">
      <div className="reset-password-form__header">
        <h2 className="reset-password-form__title">重設密碼</h2>
        <p className="reset-password-form__subtitle">
          輸入驗證碼和新密碼以完成重設
        </p>
      </div>

      <form onSubmit={handleSubmit} className="reset-password-form__form">
        {submitError && (
          <div className="reset-password-form__error" role="alert">
            {submitError}
          </div>
        )}

        <div className="reset-password-form__field">
          <label htmlFor="email" className="reset-password-form__label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`reset-password-form__input ${
              errors.email ? 'reset-password-form__input--error' : ''
            }`}
            placeholder="your@email.com"
            disabled={isSubmitting}
            autoComplete="email"
          />
          {errors.email && (
            <span className="reset-password-form__field-error">{errors.email}</span>
          )}
        </div>

        <div className="reset-password-form__field">
          <label htmlFor="code" className="reset-password-form__label">
            驗證碼 *
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            className={`reset-password-form__input ${
              errors.code ? 'reset-password-form__input--error' : ''
            }`}
            placeholder="請輸入 6 位數驗證碼"
            disabled={isSubmitting}
            maxLength={6}
            autoComplete="one-time-code"
            style={{ letterSpacing: '2px', fontFamily: 'monospace' }}
          />
          {errors.code && (
            <span className="reset-password-form__field-error">{errors.code}</span>
          )}
        </div>

        <div className="reset-password-form__field">
          <label htmlFor="newPassword" className="reset-password-form__label">
            新密碼 *
          </label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            className={`reset-password-form__input ${
              errors.newPassword ? 'reset-password-form__input--error' : ''
            }`}
            placeholder="請輸入新密碼（至少 6 個字元）"
            disabled={isSubmitting}
            autoComplete="new-password"
          />
          {errors.newPassword && (
            <span className="reset-password-form__field-error">{errors.newPassword}</span>
          )}
        </div>

        <div className="reset-password-form__field">
          <label htmlFor="confirmPassword" className="reset-password-form__label">
            確認新密碼 *
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`reset-password-form__input ${
              errors.confirmPassword ? 'reset-password-form__input--error' : ''
            }`}
            placeholder="請再次輸入新密碼"
            disabled={isSubmitting}
            autoComplete="new-password"
          />
          {errors.confirmPassword && (
            <span className="reset-password-form__field-error">{errors.confirmPassword}</span>
          )}
        </div>

        <button
          type="submit"
          className="reset-password-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? '處理中...' : '重設密碼'}
        </button>

        <div className="reset-password-form__footer">
          <button
            type="button"
            className="reset-password-form__back-link"
            onClick={handleBackToForgot}
          >
            ← 沒有收到驗證碼？
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
